import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Users, Activity, MonitorSmartphone, Flag, ToggleLeft, ToggleRight, Download, Database, FileText, RefreshCw } from "lucide-react";
import { colors } from "../theme";
import { useAuth } from "../store/useAuth";
import { apiFetch } from "../api/client";

const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

// Client-side CSV export of the loaded admin tables (no extra backend endpoint needed).
function toCSV(rows) {
  if (!rows || !rows.length) return "";
  const keys = Object.keys(rows[0]);
  const esc = (v) => {
    const str = v == null ? "" : typeof v === "object" ? JSON.stringify(v) : String(v);
    return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
  };
  return [keys.join(","), ...rows.map((r) => keys.map((k) => esc(r[k])).join(","))].join("\n");
}
function downloadCSV(filename, rows) {
  const blob = new Blob([toCSV(rows)], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function AdminPanel() {
  const { adminOpen, closeAdmin, user } = useAuth();
  const [tab, setTab] = useState("analytics");
  const [analytics, setAnalytics] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [flags, setFlags] = useState([]);
  const [newFlag, setNewFlag] = useState("");
  const [insights, setInsights] = useState(null);
  const [insightDays, setInsightDays] = useState(30);
  const [nudgeAb, setNudgeAb] = useState(null);
  const [backups, setBackups] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [backupBusy, setBackupBusy] = useState(false);
  const [dossierBusy, setDossierBusy] = useState(false);
  const [dossierResult, setDossierResult] = useState(null);
  const [dossierError, setDossierError] = useState(null);

  const reload = async () => {
    // Load every panel INDEPENDENTLY — one failing endpoint must not blank the whole dashboard.
    const eps = [
      ["/api/admin/analytics", setAnalytics],
      ["/api/admin/sessions", setSessions],
      ["/api/admin/events", setEvents],
      ["/api/admin/users", setUsers],
      ["/api/admin/flags", setFlags],
      [`/api/admin/insights?days=${insightDays}`, setInsights],
      ["/api/admin/nudge-ab", setNudgeAb],
      ["/api/admin/backups", setBackups],
    ];
    const results = await Promise.allSettled(eps.map(([p]) => apiFetch(p)));
    results.forEach((r, i) => { if (r.status === "fulfilled") eps[i][1](r.value); });
    const failed = results.filter((r) => r.status === "rejected").length;
    setLoadError(failed ? `${failed} of ${eps.length} admin feeds failed to load` : null);
  };
  useEffect(() => { if (adminOpen) reload(); }, [adminOpen]);
  // Refetch just the insights series when the day-range changes.
  useEffect(() => {
    if (!adminOpen) return;
    apiFetch(`/api/admin/insights?days=${insightDays}`).then(setInsights).catch(() => {});
  }, [insightDays, adminOpen]);

  if (!adminOpen || !user?.is_admin) return null;

  const setFlag = async (key, enabled, description) => {
    await apiFetch("/api/admin/flags", { method: "PUT", body: { key, enabled, description } });
    reload();
  };

  const exportAll = () => {
    const bundle = {
      exported_at: new Date().toISOString(),
      exported_by: user?.email,
      analytics, insights, users, sessions, events, flags,
    };
    const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `analytics-export-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const downloadBackup = async (id, createdAt) => {
    try {
      const doc = await apiFetch(`/api/admin/backups/${id}`);
      const blob = new Blob([JSON.stringify(doc.bundle, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `analytics-backup-${(createdAt || "").slice(0, 10)}.json`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch { /* ignore */ }
  };
  const runBackup = async () => {
    setBackupBusy(true);
    try { await apiFetch("/api/admin/backups/run", { method: "POST" }); await reload(); } catch { /* ignore */ }
    setBackupBusy(false);
  };
  const restoreBackup = async (id) => {
    setBackupBusy(true);
    try {
      const r = await apiFetch(`/api/admin/backups/${id}/restore`, { method: "POST" });
      if (r?.summary) setAnalytics(r.summary);
      await reload();
    } catch { /* ignore */ }
    setBackupBusy(false);
  };

  const regenerateDossier = async () => {
    setDossierBusy(true); setDossierError(null);
    try {
      const r = await apiFetch("/api/admin/dossier/regenerate", { method: "POST" });
      setDossierResult(r);
    } catch (e) {
      setDossierError(e?.message || "Dossier regeneration failed");
    }
    setDossierBusy(false);
  };

  const TABS = [["analytics", "Analytics", Activity], ["sessions", "Sessions", MonitorSmartphone], ["events", "Events", Activity], ["users", "Users", Users], ["flags", "Flags", Flag], ["backups", "Backups", Database], ["dossier", "Dossier", FileText]];

  return createPortal(
    <div style={s.overlay} data-testid="admin-panel">
      <div style={s.card}>
        <button style={s.close} onClick={closeAdmin} data-testid="admin-close"><X size={18} /></button>
        <div style={s.titleRow}>
          <div style={s.title}>Admin Dashboard <span style={s.lock}>locked</span></div>
          <button style={s.exportAllBtn} onClick={exportAll} data-testid="admin-export-all"><Download size={13} /> Export all analytics</button>
        </div>
        <div style={s.tabs}>
          {TABS.map(([id, label, Icon]) => (
            <button key={id} style={{ ...s.tab, ...(tab === id ? s.tabOn : {}) }} onClick={() => setTab(id)} data-testid={`admin-tab-${id}`}>
              <Icon size={13} /> {label}
            </button>
          ))}
        </div>

        <div style={s.body}>
          {loadError && (
            <div data-testid="admin-load-error" style={{ margin: "0 0 14px", padding: "10px 14px", borderRadius: 8, background: "#fef3f2", border: "1px solid #fecdca", color: "#b42318", fontSize: 12.5, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
              <span>{loadError}. Some panels may be incomplete.</span>
              <button data-testid="admin-retry" onClick={reload} style={{ border: "1px solid #fecdca", background: "#fff", color: "#b42318", borderRadius: 6, padding: "4px 10px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Retry</button>
            </div>
          )}
          {tab === "analytics" && !analytics && (
            <div data-testid="admin-analytics-empty" style={{ padding: "40px 16px", textAlign: "center", color: "#667085", fontSize: 13 }}>
              {loadError ? "Couldn't load analytics — tap Retry above." : "No analytics data yet for this environment."}
            </div>
          )}
          {tab === "analytics" && analytics && (
            <div data-testid="admin-analytics">
              <div style={s.kpis}>
                <Kpi n={analytics.active_sessions} label="Active now (15m)" hl />
                <Kpi n={analytics.total_visitors ?? analytics.total_users} label="Total visitors" />
                <Kpi n={analytics.registered_users ?? analytics.total_users} label="Registered" />
                <Kpi n={analytics.guest_visitors ?? 0} label="Guests" />
                <Kpi n={analytics.total_sessions} label="Sessions" />
                <Kpi n={analytics.total_events} label="Events" />
                <Kpi n={analytics.total_projects} label="Projects" />
              </div>
              {insights && (
                <div data-testid="admin-insights">
                  <div style={s.insightsHead}>
                    <div style={s.secTitle}>Login insights · last {insights.days} days</div>
                    <div style={s.rangeGroup} data-testid="insights-range">
                      {[7, 30, 90].map((d) => (
                        <button key={d} data-testid={`insights-range-${d}`} onClick={() => setInsightDays(d)}
                          style={{ ...s.rangeBtn, ...(insightDays === d ? s.rangeBtnOn : {}) }}>{d}d</button>
                      ))}
                    </div>
                  </div>
                  <MiniBars data={insights.series} valueKey="visitors" color="#0A84FF" label="Unique visitors / day (users + guests)" testId="chart-visitors" />
                  <MiniBars data={insights.series} valueKey="active_sessions" color={colors.brand} label="Active sessions / day" testId="chart-active-sessions" />
                  <MiniBars data={insights.series} valueKey="signups" color="#34A853" label="Sign-ups / day" testId="chart-signups" />
                </div>
              )}
              {nudgeAb?.variants?.length > 0 && (
                <div data-testid="admin-nudge-ab">
                  <div style={s.secTitle}>Signup nudge A/B</div>
                  <div style={s.abRow}>
                    {nudgeAb.variants.map((v) => (
                      <div key={v.variant} style={s.abCard} data-testid={`nudge-ab-${v.variant}`}>
                        <div style={s.abVariant}>Variant {v.variant}</div>
                        <div style={s.abRate}>{v.accept_rate}%</div>
                        <div style={s.abMeta}>{v.accept}/{v.shown} accepted · {v.dismiss} dismissed</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div style={s.secTitle}>Events by type</div>
              {analytics.events_by_type.map((r) => (
                <div key={r.type} style={s.barRow}>
                  <span style={s.barLabel}>{r.type}</span>
                  <span style={s.bar}><span style={{ ...s.barFill, width: `${Math.min(100, r.count / (analytics.events_by_type[0]?.count || 1) * 100)}%` }} /></span>
                  <span style={s.barN}>{r.count}</span>
                </div>
              ))}
            </div>
          )}

          {tab === "sessions" && (
            <div>
              <div style={s.exportRow}><button style={s.exportBtn} onClick={() => downloadCSV("sessions.csv", sessions)} data-testid="admin-export-sessions"><Download size={13} /> Export CSV</button></div>
              <table style={s.table} data-testid="admin-sessions"><thead><tr><th style={s.th}>User / Guest</th><th style={s.th}>Events</th><th style={s.th}>Last seen</th><th style={s.th}>Device</th></tr></thead><tbody>
                {sessions.map((x) => (<tr key={x.session_id}><td style={s.td}>{x.user_email || (x.client_id ? `guest ${String(x.client_id).slice(0, 8)}` : "—")}</td><td style={s.td}>{x.event_count || 0}</td><td style={s.td}>{fmt(x.last_seen)}</td><td style={{ ...s.td, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{x.ua}</td></tr>))}
              </tbody></table>
            </div>
          )}

          {tab === "events" && (
            <div>
              <div style={s.exportRow}><button style={s.exportBtn} onClick={() => downloadCSV("events.csv", events)} data-testid="admin-export-events"><Download size={13} /> Export CSV</button></div>
              <table style={s.table} data-testid="admin-events"><thead><tr><th style={s.th}>Type</th><th style={s.th}>User / Guest</th><th style={s.th}>When</th></tr></thead><tbody>
                {events.map((x) => (<tr key={x.id}><td style={s.td}><b>{x.type}</b></td><td style={s.td}>{x.user_email || (x.client_id ? `guest ${String(x.client_id).slice(0, 8)}` : "—")}</td><td style={s.td}>{fmt(x.created_at)}</td></tr>))}
              </tbody></table>
            </div>
          )}

          {tab === "users" && (
            <table style={s.table} data-testid="admin-users"><thead><tr><th style={s.th}>Name</th><th style={s.th}>Email</th><th style={s.th}>Provider</th><th style={s.th}>Role</th><th style={s.th}>Joined</th></tr></thead><tbody>
              {users.map((u) => (<tr key={u.user_id}><td style={s.td}>{u.name}</td><td style={s.td}>{u.email}</td><td style={s.td}>{u.provider}</td><td style={s.td}>{u.is_admin ? "admin" : "user"}</td><td style={s.td}>{fmt(u.created_at)}</td></tr>))}
            </tbody></table>
          )}

          {tab === "flags" && (
            <div data-testid="admin-flags">
              <div style={s.flagAdd}>
                <input style={s.flagInput} placeholder="new_flag_key" value={newFlag} onChange={(e) => setNewFlag(e.target.value.replace(/\s+/g, "_"))} data-testid="admin-flag-key" />
                <button style={s.flagAddBtn} disabled={!newFlag} onClick={() => { setFlag(newFlag, true, ""); setNewFlag(""); }} data-testid="admin-flag-add">Add flag (on)</button>
              </div>
              {flags.length === 0 && <div style={s.empty}>No feature flags yet. Add one to toggle features on/off for the app.</div>}
              {flags.map((f) => (
                <div key={f.key} style={s.flagRow}>
                  <div><div style={s.flagKey}>{f.key}</div>{f.description && <div style={s.flagDesc}>{f.description}</div>}</div>
                  <button style={s.flagToggle} onClick={() => setFlag(f.key, !f.enabled, f.description)} data-testid={`admin-flag-toggle-${f.key}`}>
                    {f.enabled ? <ToggleRight size={26} color={colors.brand} /> : <ToggleLeft size={26} color={colors.textTertiary} />}
                  </button>
                </div>
              ))}
            </div>
          )}

          {tab === "backups" && (
            <div data-testid="admin-backups">
              <div style={s.backupHead}>
                <div style={s.backupIntro}>Daily analytics snapshots are auto-generated and stored securely. Restore re-inserts any missing records without overwriting current data.</div>
                <button style={{ ...s.exportBtn, ...(backupBusy ? { opacity: 0.5, cursor: "wait" } : {}) }} disabled={backupBusy} onClick={runBackup} data-testid="admin-backup-run">
                  <Database size={13} /> {backupBusy ? "Working…" : "Create snapshot now"}
                </button>
              </div>
              {backups.length === 0 && <div style={s.empty}>No snapshots yet. The first daily snapshot generates automatically.</div>}
              {backups.map((b) => (
                <div key={b.id} style={s.backupRow} data-testid={`backup-row-${b.id}`}>
                  <div>
                    <div style={s.backupDate}>{fmt(b.created_at)}</div>
                    <div style={s.backupMeta}>{b.reason} · {b.counts?.users || 0} users · {b.counts?.sessions || 0} sessions · {b.counts?.events || 0} events</div>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button style={{ ...s.exportBtn, ...(backupBusy ? { opacity: 0.5, cursor: "wait" } : {}) }} disabled={backupBusy} onClick={() => restoreBackup(b.id)} data-testid={`backup-restore-${b.id}`}><Database size={13} /> Restore</button>
                    <button style={s.exportBtn} onClick={() => downloadBackup(b.id, b.created_at)} data-testid={`backup-download-${b.id}`}><Download size={13} /> Download</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === "dossier" && (
            <div data-testid="admin-dossier">
              <div style={s.backupHead}>
                <div style={s.backupIntro}>Regenerate the static dataset &amp; reference dossiers (HTML + CSVs) straight from the live v5.1 knowledge base. Use after any database change so the downloadable source-of-truth pages match what the engine computes.</div>
                <button style={{ ...s.exportBtn, ...(dossierBusy ? { opacity: 0.5, cursor: "wait" } : {}) }} disabled={dossierBusy} onClick={regenerateDossier} data-testid="admin-dossier-regenerate">
                  <RefreshCw size={13} /> {dossierBusy ? "Regenerating…" : "Regenerate dossier"}
                </button>
              </div>
              {dossierError && (
                <div data-testid="admin-dossier-error" style={{ margin: "4px 0 12px", padding: "10px 14px", borderRadius: 8, background: "#fef3f2", border: "1px solid #fecdca", color: "#b42318", fontSize: 12 }}>{dossierError}</div>
              )}
              {dossierResult?.ok && (
                <div data-testid="admin-dossier-result" style={{ margin: "4px 0 14px", padding: "10px 14px", borderRadius: 8, background: "#ecfdf3", border: "1px solid #abefc6", color: "#067647", fontSize: 12 }}>
                  Regenerated {fmt(dossierResult.generated_at)}. {dossierResult.summary}
                </div>
              )}
              <div style={s.secTitle}>Generated files</div>
              {(dossierResult?.files || ["dataset.html", "systems.csv", "crops.csv", "resources.csv", "parameters.html", "changelog.html", "references.html", "references.csv"]).map((f) => (
                <div key={f} style={s.backupRow} data-testid={`dossier-file-${f}`}>
                  <div style={s.backupDate}>{f}</div>
                  <a style={s.exportBtn} href={`/${f}`} target="_blank" rel="noreferrer" data-testid={`dossier-open-${f}`}><Download size={13} /> Open</a>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>, document.body);
}

const Kpi = ({ n, label, hl }) => (
  <div style={{ ...s.kpi, ...(hl ? { borderColor: colors.brand } : {}) }}><div style={{ ...s.kpiN, ...(hl ? { color: colors.brand } : {}) }}>{n}</div><div style={s.kpiL}>{label}</div></div>
);

// Lightweight SVG bar-trend chart (no chart lib) for the daily insights series.
const MiniBars = ({ data, valueKey, color, label, testId }) => {
  const rows = data || [];
  const max = Math.max(1, ...rows.map((d) => d[valueKey]));
  const total = rows.reduce((a, d) => a + (d[valueKey] || 0), 0);
  const W = 620, H = 84, gap = 2;
  const n = rows.length || 1;
  const bw = (W - gap * (n - 1)) / n;
  return (
    <div style={s.chartBox} data-testid={testId}>
      <div style={s.chartHead}><span style={s.chartLabel}>{label}</span><span style={s.chartTotal}>{total} total · peak {max}</span></div>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none" style={{ display: "block" }}>
        {rows.map((d, i) => {
          const v = d[valueKey] || 0;
          const h = v === 0 ? 1.5 : Math.max(2, (v / max) * (H - 14));
          return (
            <rect key={d.date} x={i * (bw + gap)} y={H - h} width={bw} height={h} rx={1.5} fill={v === 0 ? "#e5e9ee" : color}>
              <title>{d.date}: {v}</title>
            </rect>
          );
        })}
      </svg>
      <div style={s.chartAxis}><span>{rows[0]?.date?.slice(5)}</span><span>{rows[rows.length - 1]?.date?.slice(5)}</span></div>
    </div>
  );
};

const s = {
  overlay: { position: "fixed", inset: 0, background: "rgba(15,23,32,0.55)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 4000 },
  card: { position: "relative", width: 720, maxWidth: "95vw", height: "85vh", background: "#fff", borderRadius: 20, padding: "24px 26px", boxShadow: "0 24px 70px rgba(0,0,0,0.3)", fontFamily: "-apple-system, BlinkMacSystemFont, sans-serif", display: "flex", flexDirection: "column" },
  close: { position: "absolute", top: 14, right: 14, border: "none", background: "transparent", cursor: "pointer", color: colors.textTertiary },
  title: { fontSize: 19, fontWeight: 800, color: colors.textPrimary, marginBottom: 14 },
  titleRow: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginRight: 30 },
  exportAllBtn: { display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 14px", borderRadius: 999, border: "none", background: colors.brand, color: "#fff", fontSize: 11.5, fontWeight: 700, cursor: "pointer", marginBottom: 14, boxShadow: "0 4px 14px rgba(81,170,254,0.35)" },
  lock: { fontSize: 9, background: "#1c2733", color: "#fff", borderRadius: 6, padding: "2px 7px", verticalAlign: "middle", marginLeft: 6, letterSpacing: 1 },
  tabs: { display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" },
  tab: { display: "flex", alignItems: "center", gap: 6, padding: "7px 13px", borderRadius: 999, borderWidth: 1.5, borderStyle: "solid", borderColor: colors.border, background: "#fff", fontSize: 12, fontWeight: 700, color: colors.textSecondary, cursor: "pointer" },
  tabOn: { background: colors.brand, color: "#fff", borderColor: colors.brand },
  body: { flex: 1, overflowY: "auto" },
  kpis: { display: "flex", gap: 10, marginBottom: 18, flexWrap: "wrap" },
  kpi: { flex: 1, minWidth: 100, borderWidth: 1.5, borderStyle: "solid", borderColor: colors.border, borderRadius: 12, padding: "14px 12px", textAlign: "center" },
  kpiN: { fontSize: 26, fontWeight: 800, color: colors.textPrimary },
  kpiL: { fontSize: 10.5, color: colors.textTertiary, marginTop: 2 },
  secTitle: { fontSize: 11, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase", color: colors.textTertiary, margin: "6px 0 10px" },
  barRow: { display: "flex", alignItems: "center", gap: 10, marginBottom: 7 },
  barLabel: { fontSize: 11.5, color: colors.textSecondary, width: 150, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  bar: { flex: 1, height: 10, background: "#eef1f4", borderRadius: 999, overflow: "hidden" },
  barFill: { display: "block", height: "100%", background: colors.brand, borderRadius: 999 },
  barN: { fontSize: 11.5, fontWeight: 700, color: colors.textPrimary, width: 40, textAlign: "right" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 11.5 },
  th: { textAlign: "left", padding: "8px 10px", borderBottom: `2px solid ${colors.border}`, color: colors.textTertiary, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5, position: "sticky", top: 0, background: "#fff" },
  td: { padding: "8px 10px", borderBottom: `1px solid ${colors.border}`, color: colors.textPrimary },
  flagAdd: { display: "flex", gap: 8, marginBottom: 14 },
  flagInput: { flex: 1, border: `1.5px solid ${colors.border}`, borderRadius: 10, padding: "9px 12px", fontSize: 12.5, outline: "none" },
  flagAddBtn: { border: "none", background: colors.brand, color: "#fff", borderRadius: 10, padding: "0 16px", fontSize: 12, fontWeight: 700, cursor: "pointer" },
  flagRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 14px", border: `1.5px solid ${colors.border}`, borderRadius: 12, marginBottom: 8 },
  flagKey: { fontSize: 13, fontWeight: 700, color: colors.textPrimary, fontFamily: "monospace" },
  flagDesc: { fontSize: 11, color: colors.textTertiary },
  flagToggle: { border: "none", background: "transparent", cursor: "pointer", display: "flex" },
  empty: { fontSize: 12, color: colors.textTertiary, fontStyle: "italic", padding: "10px 0" },
  chartBox: { marginBottom: 16, padding: "12px 14px", borderRadius: 12, borderWidth: 1.5, borderStyle: "solid", borderColor: colors.border, background: "#fff" },
  chartHead: { display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 },
  chartLabel: { fontSize: 12, fontWeight: 700, color: colors.textPrimary },
  chartTotal: { fontSize: 10.5, color: colors.textTertiary },
  chartAxis: { display: "flex", justifyContent: "space-between", marginTop: 4, fontSize: 9.5, color: colors.textTertiary },
  exportRow: { display: "flex", justifyContent: "flex-end", marginBottom: 8 },
  exportBtn: { display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 13px", borderRadius: 999, border: `1.5px solid ${colors.border}`, background: "#fff", fontSize: 11.5, fontWeight: 700, color: colors.textSecondary, cursor: "pointer" },
  insightsHead: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 },
  rangeGroup: { display: "flex", gap: 4, background: "#eef1f4", borderRadius: 999, padding: 3, marginBottom: 6 },
  rangeBtn: { border: "none", background: "transparent", borderRadius: 999, padding: "4px 11px", fontSize: 11, fontWeight: 700, color: colors.textSecondary, cursor: "pointer" },
  rangeBtnOn: { background: "#fff", color: colors.brand, boxShadow: "0 1px 3px rgba(0,0,0,0.12)" },
  abRow: { display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 },
  abCard: { flex: 1, minWidth: 130, borderWidth: 1.5, borderStyle: "solid", borderColor: colors.border, borderRadius: 12, padding: "12px 14px" },
  abVariant: { fontSize: 10.5, fontWeight: 800, letterSpacing: 0.6, textTransform: "uppercase", color: colors.textTertiary },
  abRate: { fontSize: 24, fontWeight: 800, color: colors.brand, margin: "2px 0" },
  abMeta: { fontSize: 10.5, color: colors.textTertiary },
  backupHead: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 12 },
  backupIntro: { fontSize: 11.5, color: colors.textTertiary, flex: 1, lineHeight: 1.45 },
  backupRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 14px", borderWidth: 1.5, borderStyle: "solid", borderColor: colors.border, borderRadius: 12, marginBottom: 8 },
  backupDate: { fontSize: 13, fontWeight: 700, color: colors.textPrimary },
  backupMeta: { fontSize: 11, color: colors.textTertiary, marginTop: 2 },
};

